import type { Repository } from '@/shared/Repository'
import type { Session } from 'next-auth'
import { editorialInfoDocumentTemplate } from '@/shared/templates/editorialInfoDocumentTemplate'
import { renderMemo } from '@/shared/templates/memos/lib/renderMemo'
import type { MemoItem } from '@/shared/templates/memos/lib/renderMemo'
import type { InsertedMemo } from '@/shared/templates/memos/lib/insertedDocument'
import type { MemoTemplate, MemoVariant } from '@/shared/templates/memos'
import { addAssignmentWithDeliverable } from '@/lib/index/addAssignment'
import { Block } from '@ttab/elephant-api/newsdoc'

/**
 * Create an auto-generated memo.
 *
 * The rendered memo is saved as a core/editorial-info document first, then
 * linked in as an editorial-info assignment - the same order the rest of the
 * app uses, so the planning never points at a deliverable that does not exist
 * yet. If `planningId` is given the assignment is appended to that existing
 * planning; otherwise a brand new planning is created from `planningTitle`.
 *
 * The memo is deliberately only ever saved as a draft; no status is set, so it
 * is never published by this flow.
 *
 * `assignmentTitle` titles the assignment and the editorial-info document;
 * `memoTitle` is the memo's first heading, and `planningTitle` titles a newly
 * created planning. The backend rejects a new planning without both a section
 * and a priority, so both are required here.
 *
 * `memoTitle` is separate from `assignmentTitle` because the heading a PM goes
 * out under is not the same concern as what the work is filed as. The template
 * suggests the same string to both, and they part company either because it
 * declares a heading of its own - a print PM's assignment is called "PM-BNL"
 * after the type it is about, while the PM it delivers is still headed with the
 * day it covers - or because the editor wrote over the heading field in the
 * preview, which renames neither the assignment nor the planning.
 *
 * `planningTitle` is the one of the three that can be absent: a memo filed into
 * an existing planning titles no planning, but is still headed with something.
 *
 * `slugline` tags the assignment (and the editorial-info document itself);
 * `planningSlugline` tags a newly created planning separately - the two are
 * independent and only the former is ever required, since the latter has no
 * effect when appending to an existing planning.
 *
 * `variant` is the shape a template that comes in more than one is written as,
 * and has to reach the render as well as the titles - it carries the memo's
 * intro line.
 *
 * `inserted` is the already written PMs the editor folded in, each rendered
 * whole at the end of the block it was put in.
 *
 * `writtenForSection` is the section the memo is written for, by title, which is
 * not `section` - that one is where the memo is filed, and is the desk's own for
 * a memo written for no section at all. It reaches the render because a memo
 * whose title names its section does not repeat it in its body.
 *
 * Resolves to the planning the memo was filed into, which is the one a memo
 * written alongside it goes into as well.
 */
export async function createMemo({
  repository,
  session,
  id,
  template,
  items,
  planningId,
  planningTitle,
  assignmentTitle,
  memoTitle,
  section,
  newsvalue,
  language,
  localDate,
  isoDateTime,
  author,
  slugline,
  planningSlugline,
  editorialInfoType,
  variant,
  inserted,
  writtenForSection
}: {
  repository: Repository | undefined
  session: Session | null
  id: string
  template: MemoTemplate
  items: MemoItem[]
  planningId?: string
  planningTitle?: string
  assignmentTitle: string
  memoTitle: string
  section: { uuid: string, title: string }
  newsvalue: string
  language?: string
  localDate: string
  isoDateTime: string
  author?: { id: string, name: string }
  slugline: string
  planningSlugline?: string
  editorialInfoType?: { uuid: string }
  variant?: MemoVariant
  inserted?: InsertedMemo[]
  writtenForSection?: string
}): Promise<string> {
  if (!session?.accessToken || !repository) {
    throw new Error('Cannot create memo: missing session or repository')
  }

  const trimmedSlugline = slugline.trim()

  const document = editorialInfoDocumentTemplate(id, {
    title: assignmentTitle,
    content: renderMemo(template, items, memoTitle, {
      variant,
      inserted,
      sectionTitle: writtenForSection
    }),
    ...(language ? { language } : {}),
    meta: { 'tt/slugline': [Block.create({ type: 'tt/slugline', value: trimmedSlugline })] },
    links: {
      'core/section': [Block.create({
        type: 'core/section',
        rel: 'section',
        uuid: section.uuid,
        title: section.title
      })],
      ...(editorialInfoType
        ? {
            'core/editorial-info-type': [Block.create({
              uuid: editorialInfoType.uuid,
              type: 'tt/editorial-info-type',
              rel: 'info-type'
            })]
          }
        : {})
    }
  })

  await repository.saveDocument(document, session.accessToken)

  const linkedPlanningId = await addAssignmentWithDeliverable({
    planningId,
    planningTitle,
    type: 'editorial-info',
    deliverableId: id,
    title: assignmentTitle,
    slugline: trimmedSlugline,
    planningSlugline: planningSlugline?.trim(),
    section,
    priority: Number(newsvalue),
    publicVisibility: true,
    localDate,
    isoDateTime,
    author
  })

  if (!linkedPlanningId) {
    throw new Error('Cannot create memo: planning link failed')
  }

  return linkedPlanningId
}
